import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import api from '../services/api';
import Button from '../components/Button';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import Layout from '../components/Layout';
import './ProjectDetails.css';

function ProjectDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('orgId');
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [crawls, setCrawls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingCrawl, setStartingCrawl] = useState(false);
  const [crawlProgress, setCrawlProgress] = useState({});

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (!orgId) {
      navigate('/dashboard');
      return;
    }

    loadProject();
    loadCrawls();
    
    // Poll for crawl status updates every 3 seconds
    const interval = setInterval(() => {
      loadCrawls();
      loadProject();
    }, 3000);

    return () => clearInterval(interval);
  }, [id, orgId]);

  const loadProject = async () => {
    try {
      const response = await api.get(`/projects/${id}?organizationId=${orgId}`);
      setProject(response.data.project);
    } catch (error) {
      console.error('Error loading project:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadCrawls = async () => {
    try {
      const response = await api.get(`/crawls/projects/${id}/crawls?organizationId=${orgId}`);
      const crawlsData = response.data.crawls || [];
      setCrawls(crawlsData);
      
      // Load progress for active crawls - use progress from crawl data or fetch detailed status
      for (const crawl of crawlsData) {
        const status = (crawl.status || '').toLowerCase();
        if (status === 'crawling' || status === 'queued' || status === 'analyzing') {
          // Use progress from crawl data if available
          if (crawl.progress) {
            setCrawlProgress(prev => ({
              ...prev,
              [crawl.id]: crawl.progress
            }));
          } else {
            // Otherwise fetch detailed status
            try {
              const statusResponse = await api.get(`/crawls/${crawl.id}/status?organizationId=${orgId}`);
              if (statusResponse.data.crawl?.progress) {
                setCrawlProgress(prev => ({
                  ...prev,
                  [crawl.id]: statusResponse.data.crawl.progress
                }));
              }
            } catch (err) {
              // Ignore errors loading progress
              console.error('Error loading crawl progress:', err);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading crawls:', error);
    }
  };


  const handleStartCrawl = async () => {
    setStartingCrawl(true);
    try {
      const response = await api.post(`/crawls/projects/${id}/crawls/start`, {
        organizationId: orgId,
        settings: {
          maxPages: 200,
          maxDepth: 3,
          respectRobots: true
        }
      });
      await loadCrawls();
      await loadProject();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to start crawl');
    } finally {
      setStartingCrawl(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="error-container">
        <h2>Project not found</h2>
        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <Layout>
      <div className="project-details-page">
        <div className="page-header">
          <div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')} size="small" style={{ marginBottom: '12px' }}>
              ← Back
            </Button>
            <h1 className="page-title">{project.name}</h1>
            <p className="page-subtitle">{project.rootUrl}</p>
          </div>
          <div className="header-actions">
            <Button variant="primary" onClick={handleStartCrawl} loading={startingCrawl} className="gradient-btn">
              {crawls.length > 0 ? 'Re-run Crawl' : 'Start Crawl'}
            </Button>
          </div>
        </div>

        <div className="details-content">
        <div className="details-layout">
          <div className="project-info-section">
            <div className="details-card compact-card">
              <h3>Project Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">URL</span>
                  <span className="info-value">{project.rootUrl}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Created</span>
                  <span className="info-value">
                    {new Date(project.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="crawl-history-section">
            <div className="details-card">
              <div className="crawl-header">
                <h3>Crawl History</h3>
                {crawls.length > 0 && (
                  <span className="crawl-count">{crawls.length} crawl{crawls.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              {crawls.length === 0 ? (
                <div className="empty-crawls">
                  <p>No crawls yet</p>
                  <p className="hint">Start your first crawl to begin auditing</p>
                </div>
              ) : (
                <div className="crawls-table">
                  <div className="crawls-table-header">
                    <div className="col-crawl">Crawl</div>
                    <div className="col-date">Date & Time</div>
                    <div className="col-pages">Pages</div>
                    <div className="col-status">Status</div>
                  </div>
                  <div className="crawls-table-body">
                    {crawls.map((crawl) => {
                      const progress = crawlProgress[crawl.id] || crawl.progress;
                      const status = (crawl.status || '').toLowerCase();
                      const isActive = status === 'crawling' || status === 'queued' || status === 'analyzing';
                      
                      return (
                        <div 
                          key={crawl.id} 
                          className="crawl-row" 
                          onClick={() => navigate(`/crawl/${crawl.id}?orgId=${orgId}`)}
                        >
                          <div className="col-crawl">
                            <span className="crawl-number">#{crawl.id}</span>
                          </div>
                          <div className="col-date">
                            <span className="date-text">
                              {new Date(crawl.createdAt || crawl.created_at).toLocaleDateString()}
                            </span>
                            <span className="time-text">
                              {new Date(crawl.createdAt || crawl.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div className="col-pages">
                            {isActive && progress ? (
                              <div className="pages-progress">
                                <span className="pages-count">
                                  {progress.pagesCrawled || crawl.pageCount || 0} / {progress.maxPages || 200}
                                </span>
                                <div className="mini-progress-bar">
                                  <div 
                                    className="mini-progress-fill" 
                                    style={{ width: `${progress.progressPercent || 0}%` }}
                                  ></div>
                                </div>
                              </div>
                            ) : (
                              <span className="pages-count">{crawl.pageCount || 0}</span>
                            )}
                          </div>
                          <div className="col-status">
                            <span className={`status-badge status-${status || 'pending'}`}>
                              {(crawl.status || 'PENDING').toUpperCase()}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ProjectDetails;

